'use client';

import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { stegaClean } from '@sanity/client/stega';
import type { IFlashCampaign, IFlashSale } from '@/sanity/lib/definitions';
import { useLiveFlashCampaign } from '@/hooks/useFlashSaleLive';

export interface IFlashSaleContext {
  config: IFlashSale | null;
  /** True while any campaign (flash pair or seasonal entry) is live. */
  live: boolean;
  /** The live campaign whose banner pair the page should show, or null. */
  campaign: IFlashCampaign | null;
}

// Default is a disabled campaign so components using useFlashSale() outside
// the provider (storybook-ish renders, isolated templates) behave exactly as
// before the feature existed.
const FlashSaleContext = createContext<IFlashSaleContext>({
  config: null,
  live: false,
  campaign: null,
});

/**
 * Provides the Flash Sale campaign (fetched once per page via BasePageQuery)
 * plus its client-side live state to every component that displays banners,
 * discounts or coupon CTAs. Mounted once in app/[[...slug]]/page.tsx so all
 * templates and languages share the same state.
 */
export default function FlashSaleProvider({
  config,
  children,
}: {
  config: IFlashSale | null | undefined;
  children: ReactNode;
}) {
  // Draft/Presentation mode fetches with stega enabled — invisible characters
  // inside startDate/endDate/coupon strings would break Date parsing and the
  // coupon equality guard (same reason useCTAWrapper cleans its CTA).
  const cleanedConfig = useMemo(
    () => (stegaClean(config ?? null) ?? null) as IFlashSale | null,
    [config],
  );
  const campaign = useLiveFlashCampaign(cleanedConfig);
  const value = useMemo<IFlashSaleContext>(
    () => ({ config: cleanedConfig, live: campaign !== null, campaign }),
    [cleanedConfig, campaign],
  );
  return (
    <FlashSaleContext.Provider value={value}>{children}</FlashSaleContext.Provider>
  );
}

export function useFlashSale(): IFlashSaleContext {
  return useContext(FlashSaleContext);
}
